import { cn, cssVars } from "@sagot/ui";
import type { ReactNode } from "react";
import { meterView } from "./billing-model";

interface UsageMeterProps {
  label: string;
  used: number;
  limit: number | null;
  /** Visible readout pieces, e.g. "6,240" and "/ 10,000". */
  usedLabel: string;
  limitLabel: string;
  projected?: number;
  hint?: ReactNode;
  /** Hint turns warning-coloured (paired with its text, never colour alone). */
  hintWarns?: boolean;
}

/** Billing usage meter (mockup: bold label, `used / limit`, bar, hint line). */
export function UsageMeter({ label, used, limit, usedLabel, limitLabel, projected, hint, hintWarns }: UsageMeterProps) {
  const view = meterView(used, limit, projected);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-bold">{label}</span>
        <span className="text-sm tabular-nums">
          <strong>{usedLabel}</strong> <span className="text-fg-subtle">{limitLabel}</span>
        </span>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit ?? Math.max(used, 1)}
        aria-valuenow={used}
        aria-valuetext={`${usedLabel} ${limitLabel}`}
        className="mt-1.5 h-2 rounded-pill bg-sunken"
      >
        <div className={cn("h-2 w-[var(--geo-width)] rounded-pill", view.warn ? "bg-accent" : "bg-primary")} style={cssVars({ "--geo-width": `${String(view.percent)}%` })} />
      </div>
      {hint ? <p className={cn("mt-1 text-xs", hintWarns ? "text-warning" : "text-fg-subtle")}>{hint}</p> : null}
    </div>
  );
}
